import { useQuery } from '@tanstack/react-query';
import { awaitJSON, SlidesLambdaFetch } from '../../utils/fetch-utils';

export interface InputDefinition {
  inputName: string;
  // Present on server data but not maintained client-side: the editor tracks order purely by
  // array position, and saveInputGroupings recomputes it from that position on write.
  inputOrder?: number;
}

export interface InputGroup {
  groupId: string;
  label: string;
  // See InputDefinition.inputOrder — derived from array position on save, not tracked here.
  groupOrder?: number;
  inputs: InputDefinition[];
}

export interface Slide {
  slideId: string;
  slideNumber: number;
  imageUrl: string;
  dynamicContentName: string;
  inputGroups: InputGroup[];
}

export interface Presentation {
  templateId: string;
  templateName: string;
  slides: Slide[];
}

interface SlideInputGroup {
  presentationId: string;
  slideId: string;
  label: string;
  groupOrder: number;
}

interface SlideInputGroupMapping {
  dynamicContentName: string;
  inputName: string;
  inputOrder: number;
  groupLabel: string;
  slideId: string;
}

interface GroupWithMappings {
  group: SlideInputGroup;
  mappings: SlideInputGroupMapping[];
}

export const UNGROUPED_LABEL = 'Ungrouped';

const TEMPLATE_INPUT_GROUPINGS_QUERY_KEY = ['templateInputGroupings'];

const loadTemplateInputGroupings = async (): Promise<Presentation[]> => {
  const res = await SlidesLambdaFetch('/getTemplateInputGroupings');
  return awaitJSON<Presentation[]>(res);
};

export const useTemplateInputGroupings = (): ReturnType<typeof useQuery<Presentation[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: TEMPLATE_INPUT_GROUPINGS_QUERY_KEY,
    queryFn: loadTemplateInputGroupings
  });

export const saveInputGroupings = async (presentation: Presentation): Promise<void> => {
  const updates: GroupWithMappings[] = [];

  presentation.slides.forEach(slide => {
    // Only process inputs that are grouped
    const groups = slide.inputGroups.filter(group => group.label !== UNGROUPED_LABEL);

    groups.forEach((group, groupIndex) => {
      const editGroup: SlideInputGroup = {
        presentationId: presentation.templateId,
        slideId: slide.slideId,
        label: group.label,
        groupOrder: groupIndex + 1
      };

      // Mappings for each input in the group
      const mappings: SlideInputGroupMapping[] = group.inputs.map((input, inputIndex) => ({
        dynamicContentName: slide.dynamicContentName,
        inputName: input.inputName,
        inputOrder: inputIndex + 1,
        groupLabel: group.label,
        slideId: slide.slideId
      }));

      updates.push({ group: editGroup, mappings });
    });
  });

  await SlidesLambdaFetch('/updateInputGroupings', {
    method: 'POST',
    body: { presentationId: presentation.templateId, updates }
  });
};
